import { NavLink, Navigate, Outlet, Route, Routes, useLocation, useParams } from "react-router-dom";
import { useLogout, useMe, type Me } from "./api/auth";
import HomePage from "./pages/HomePage";
import ProcessingPage from "./pages/ProcessingPage";
import RecordingsPage from "./pages/RecordingsPage";
import FraudPage from "./pages/FraudPage";
import ClipsPage from "./pages/ClipsPage";
import ClipViewPage from "./pages/ClipViewPage";
import DetailPage from "./pages/DetailPage";
import UploadPage from "./pages/UploadPage";
import UsersPage from "./pages/UsersPage";
import PaymentsPage from "./pages/PaymentsPage";
import LeadsPage from "./pages/LeadsPage";
import AppTasksPage from "./pages/AppTasksPage";
import LoginPage from "./pages/LoginPage";
import BrandLogo from "./components/BrandLogo";
import {
  ClipsIcon,
  FraudIcon,
  HomeIcon,
  LeadsIcon,
  PaymentsIcon,
  ProcessingIcon,
  RecordingsIcon,
  SignOutIcon,
  TasksIcon,
  UploadIcon,
  UsersIcon,
} from "./components/NavIcons";
import { usePaymentsSummary } from "./api/payments";
import { useRecordings } from "./api/recordings";

function LegacyDetailRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/r/${id}`} replace />;
}

/** Operator-only pages (pipeline, uploads): a demo session is sent back Home. */
function OperatorOnly({ children }: { children: React.ReactElement }) {
  const me = useMe();
  return me.data?.demo ? <Navigate to="/" replace /> : children;
}

/** Gate: render the app shell only with a valid session; otherwise bounce to /login. */
function RequireAuth() {
  const me = useMe();
  const location = useLocation();
  if (me.isPending) return <div className="auth-loading">Loading…</div>;
  if (me.isError) return <Navigate to="/login" replace state={{ from: location }} />;
  return <Shell me={me.data} />;
}

function NavItem({
  to,
  end,
  icon,
  label,
  count,
}: {
  to: string;
  end?: boolean;
  icon: React.ReactNode;
  label: string;
  count?: number;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) => (isActive ? "side-link active" : "side-link")}
      title={label}
    >
      {icon}
      <span className="side-label">{label}</span>
      {count != null && count > 0 && <span className="side-count">{count}</span>}
    </NavLink>
  );
}

/** App shell: light grouped sidebar (Review / Operations / Admin) + content column. */
function Shell({ me }: { me: Me }) {
  const logout = useLogout();
  // badge counts: what needs attention. Cheap queries (limit=1 just for `total`; the
  // payments summary is already the page's payload).
  const pending = useRecordings({ status: "pending_review", limit: 1 });
  const suspects = useRecordings({ status: "potential_fraud", limit: 1 });
  const payments = usePaymentsSummary();
  const initial = (me.username[0] ?? "?").toUpperCase();

  return (
    <div className="app">
      <aside className="sidebar" aria-label="Main navigation">
        <NavLink to="/" className="brand">
          <BrandLogo className="brand-logo" />
          <span className="brand-word">
            Gravando<span className="brand-accent">&nbsp;AI</span>
          </span>
        </NavLink>

        <nav className="sidebar-nav">
          <div className="side-group">
            <div className="side-group-label">Review</div>
            <NavItem to="/" end icon={<HomeIcon />} label="Home" />
            <NavItem to="/recordings" icon={<RecordingsIcon />} label="Recordings" count={pending.data?.total} />
            <NavItem to="/fraud" icon={<FraudIcon />} label="Fraud Detection" count={suspects.data?.total} />
            <NavItem to="/clips" icon={<ClipsIcon />} label="Clips" />
          </div>
          {!me.demo && (
            <div className="side-group">
              <div className="side-group-label">Operations</div>
              <NavItem to="/processing" icon={<ProcessingIcon />} label="Processing" />
              <NavItem to="/upload" icon={<UploadIcon />} label="Manual Upload" />
            </div>
          )}
          <div className="side-group">
            <div className="side-group-label">Admin</div>
            <NavItem to="/users" icon={<UsersIcon />} label="Users" />
            <NavItem to="/payments" icon={<PaymentsIcon />} label="Payments" count={payments.data?.totals.users_waiting} />
            <NavItem to="/leads" icon={<LeadsIcon />} label="Leads" />
            <NavItem to="/app-tasks" icon={<TasksIcon />} label="App Tasks" />
          </div>
        </nav>

        <div className="side-me">
          <span className="side-avatar" aria-hidden>{initial}</span>
          <span className="side-me-text">
            <b>Admin</b>
            <span title={me.username}>{me.username}</span>
          </span>
          <button
            className="side-logout"
            onClick={() => logout.mutate()}
            disabled={logout.isPending}
            title="Sign out"
            aria-label="Sign out"
          >
            <SignOutIcon />
          </button>
        </div>
      </aside>
      <main className="content">
        <Outlet />
      </main>
    </div>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route element={<RequireAuth />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/recordings" element={<RecordingsPage />} />
        <Route path="/fraud" element={<FraudPage />} />
        <Route path="/clips" element={<ClipsPage />} />
        <Route path="/clips/:id" element={<ClipViewPage />} />
        <Route path="/processing" element={<OperatorOnly><ProcessingPage /></OperatorOnly>} />
        <Route path="/r/:id" element={<DetailPage />} />
        <Route path="/recordings/:id" element={<LegacyDetailRedirect />} />
        <Route path="/upload" element={<OperatorOnly><UploadPage /></OperatorOnly>} />
        <Route path="/users" element={<UsersPage />} />
        <Route path="/payments" element={<PaymentsPage />} />
        <Route path="/leads" element={<LeadsPage />} />
        <Route path="/app-tasks" element={<AppTasksPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
